import React, { useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControlLabel, 
    Switch, 
    CircularProgress, 
    Grid 
} from '@mui/material';
import { RestaurantService } from '../../../api/services/restaurantService';
import { toast } from '../../../utils/toast';

export default function MenuCategoryModal({ open, onClose, onCategoryCreated }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: '',
        displayOrder: '',
        isActive: true
    });

    const resetForm = () => {
        setForm({
            name: '',
            displayOrder: '',
            isActive: true
        });
    };

    const handleClose = () => {
        if (isSubmitting) return;
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await RestaurantService.createMenuCategory({
                name: form.name,
                displayOrder: form.displayOrder === '' ? 0 : Number(form.displayOrder),
                isActive: Boolean(form.isActive)
            });
            toast.success('New menu category successfully indexed and published');
            handleClose();
            if (onCategoryCreated) {
                onCategoryCreated();
            }
        } catch (error) {
            console.error('Failed to create menu category:', error);
            // Validation rejections/errors handled safely by global interceptor
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle fontWeight="bold">Create New Category</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            placeholder="e.g., Breakfast, Appetizers"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            disabled={isSubmitting}
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Display Order"
                            placeholder="0"
                            value={form.displayOrder}
                            onChange={(e) => {
                                let val = e.target.value;
                                if (val.length > 1 && val.startsWith('0')) {
                                    val = val.replace(/^0+/, '');
                                    if (val === '') val = '0';
                                }
                                if (val !== '' && Number(val) < 0) {
                                    val = '0';
                                }
                                setForm({ ...form, displayOrder: val });
                            }}
                            disabled={isSubmitting}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                            helperText="Priority sorting rank on consumer menus"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                    color="success"
                                    disabled={isSubmitting}
                                />
                            }
                            label="Active Visibility (Instantly Publish)"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={isSubmitting} 
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit}
                    disabled={isSubmitting || !form.name.trim()}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Creating...' : 'Create Category'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
